import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AutocompleteItemModel, ProductAutocompleteItemModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getAllProductsState } from "@rightstore/Store/Data/AllProducts/AllProductsSelectors";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface AutocompleteProductsStyles {
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    type?: TypographyPresentationProps;
    typesWrapper?: InjectableCss;
}

type CustomApplicationState = ApplicationState & {
    allProducts: {
        products: ProductModel[];
    };
};

const mapStateToProps = (state: ApplicationState) => {
    const allProducts = getAllProductsState(state as CustomApplicationState);

    return {
        allProducts
    };
};

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            padding: 8px 16px;
            cursor: pointer;

            &:hover {
                background-color: #F2F2F2;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 8px 0px;
                        `,
                        css`
                            padding: 8px 0px;
                        `,
                        null,
                        null,
                        null,
                    ],
                )
            }
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            padding: 8px 16px;
            cursor: pointer;
            background-color: #F2F2F2;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 8px 0px;
                        `,
                        css`
                            padding: 8px 0px;
                        `,
                        null,
                        null,
                        null,
                    ],
                )
            }
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            weight: 400,
            color: "#006FF8",
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
                weight: 400px !important;
                color: #006FF8 !important;
                max-width: calc(457px - 32px);

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                max-width: 75vw;
                            `,
                            css`
                                max-width: 75vw;
                            `,
                            null,
                            null,
                            null,
                        ],
                    )
                }
            `,
        },
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
    type: {
        css: css`
            margin-right: 16px;
            weight: 400;
            font-size: 15px;
            line-height: 22px;
            color: #666;
            white-space: nowrap;
        `
    },
    typesWrapper: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
            overflow-x: hidden;
            max-width: calc(457px - 32px);

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 75vw;
                        `,
                        css`
                            max-width: 75vw;
                        `,
                        null,
                        null,
                        null,
                    ],
                )
            }
        `
    }
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products: productSearchResults, allProducts } = this.props;
        const styles = this.styles;

        return (
            <>
                {productSearchResults.map((productSearchResult, index) => {
                    const fullProductData = allProducts?.find((product) => product.id === productSearchResult.id);
                    const attributeTypes = (fullProductData
                        && fullProductData.attributeTypes
                        && fullProductData?.attributeTypes[0])
                        ? fullProductData?.attributeTypes[0].attributeValues
                        : [];

                    if (!productSearchResult?.id) { return null; }

                    return (
                        <GridContainer
                            {...(this.props.focusedItem === productSearchResult ? styles.focusedContainer : styles.container)}
                            key={productSearchResult.id}
                            onClick={() => {
                                this.props.goToUrl(productSearchResult.url);
                            }}
                            className="autocomplete-products-container"
                        >
                            <GridItem {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                                <Link
                                    {...styles.titleLink}
                                    data-test-selector={`productId_${productSearchResult.id}`}
                                    className="autocomplete-products-link"
                                >
                                    {productSearchResult.displayTitle}
                                </Link>
                                <StyledWrapper {...styles.typesWrapper}>
                                    {attributeTypes?.map((type) => {
                                        return (
                                            <Typography
                                                key={`${productSearchResult.id}-${type.valueDisplay}`}
                                                {...styles.type}
                                                data-test-selector="autocompleteProducts_PartNumber"
                                                className="autocomplete-products-erp-number"
                                            >
                                                {type.valueDisplay}
                                            </Typography>
                                        )
                                    })}
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    )
                }
                )}
            </>
        );
    }
}

export default connect(mapStateToProps)(AutocompleteProducts);
